<script setup lang="ts">
  import { DocumentsList, DocumentsUpload } from '@/components/app/documents'
  import { useTranslation } from '@/composables/useTranslation'
  import type { AIChatSession } from '@/types/generated'
  import { AIChatSessionTypeEnum } from '@/types/generated'
  import { ChevronLeft } from 'lucide-vue-next'
  import { inject, ref, type Ref } from 'vue'

  const { t } = useTranslation({
    en: { Subtitle: 'AI chat has access to all documents in this chat.' },
    de: { Subtitle: 'Der KI-Chat hat Zugriff auf alle Dokumente in diesem Chat.' }
  })

  withDefaults(
    defineProps<{
      title: string
      count?: number
      documentProcessor: any
      showDocumentsList?: boolean
    }>(),
    { showDocumentsList: true }
  )

  const emit = defineEmits<{ back: [] }>()

  // Inject context needed to create a session before the first file upload in global chat
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const sessionGroupData = inject<Ref<any>>('sessionGroupData', ref(null))
  const createNewChatSession = inject<(sessionGroupUuid?: string) => Promise<AIChatSession | null>>(
    'createNewChatSession',
    async () => null
  )

  const handleFilesSelected = async (files: File[]) => {
    if (
      sessionType === AIChatSessionTypeEnum.GLOBAL &&
      !activeChatSession.value &&
      files.length > 0
    ) {
      try {
        const sessionGroupUuid = sessionGroupData.value?.uuid || undefined
        await createNewChatSession(sessionGroupUuid)
      } catch (error) {
        console.error('Failed to create chat session for file upload:', error)
      }
    }
  }
</script>

<template>
  <div class="flex flex-col">
    <!-- Header -->
    <div class="relative flex items-center border-b border-gray-100 px-4 py-3">
      <button
        type="button"
        class="shrink-0 text-gray-500 hover:text-gray-700"
        @click="emit('back')"
      >
        <ChevronLeft class="size-5" />
      </button>
      <div class="absolute left-1/2 flex -translate-x-1/2 items-center gap-2">
        <span class="text-sm font-semibold text-gray-800">{{ title }}</span>
        <span
          v-if="count"
          class="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-800"
        >
          {{ count }}
        </span>
      </div>
    </div>
    <p class="px-4 pb-1 pt-2 text-center text-xs text-gray-500">{{ t('Subtitle') }}</p>
    <!-- Document list -->
    <div
      v-if="showDocumentsList"
      class="max-h-[314px] overflow-y-auto px-2 py-1"
    >
      <DocumentsList
        :document-processor="documentProcessor"
        :show-delete="false"
        :show-edit="false"
        :compact="true"
      />
    </div>
    <!-- Upload area -->
    <div class="border-t border-gray-100 p-4">
      <DocumentsUpload
        :document-processor="documentProcessor"
        :compact="true"
        @files-selected="handleFilesSelected"
      />
    </div>
  </div>
</template>
